import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import DateRangePicker from '@/components/molecules/DateRangePicker/DateRangePicker';
import { addAuditFilters, applyAuditFilterChange } from '@/domain/auditLog';

import FilterTextField from '../FilterTextField';

import ActionPicker from './ActionPicker';
import FilterMenu from './FilterMenu';
import type { AuditFilterBindings } from './filterBindings';
import { outcomeSections } from './filterOptions';
import ScopePicker from './ScopePicker';
import { pickerRange, withPickedRange } from './timeRange';

import styles from './AuditLogToolbar.module.scss';

interface AuditLogToolbarProps {
    bindings: AuditFilterBindings;
    /** Every active filter, the ones shown here included. */
    activeCount: number;
    panelId: string;
    isPanelOpen: boolean;
    onTogglePanel: () => void;
}

/**
 * The filters most investigations start from, on one line at 1440 px. Everything else,
 * these included, is in the All filters panel.
 */
export default function AuditLogToolbar(props: AuditLogToolbarProps) {
    const { bindings, activeCount, panelId, isPanelOpen, onTogglePanel } = props;
    const { filters, onChange, onType } = bindings;
    const { t } = useTranslation();
    const range = pickerRange(filters);

    return (
        <fieldset className={styles.toolbar} aria-label={t('auditLog.filters.toolbar')}>
            <DateRangePicker
                dateFrom={range.dateFrom}
                dateTo={range.dateTo}
                defaultPresetId="all"
                onDateChange={(picked, change) =>
                    onChange(withPickedRange(filters, picked, change))
                }
            />
            <div className={styles.person}>
                <FilterTextField
                    field="actorEmail"
                    label={t('auditLog.filters.person')}
                    placeholder={t('auditLog.filters.personPlaceholder')}
                    filters={filters}
                    onType={onType}
                    inline
                />
            </div>
            <ScopePicker bindings={bindings} label={t('auditLog.filters.where')} />
            <ActionPicker bindings={bindings} label={t('auditLog.filters.actions')} />
            <FilterMenu
                label={t('auditLog.filters.outcome')}
                options={outcomeSections(t)}
                selected={filters.outcome}
                onChange={(outcome) =>
                    onChange(applyAuditFilterChange(filters, addAuditFilters({ outcome })))
                }
            />
            <Button
                variant="secondary"
                type="button"
                className={styles.allFilters}
                aria-expanded={isPanelOpen}
                aria-controls={panelId}
                onClick={onTogglePanel}>
                {t('auditLog.filters.allFilters')}{' '}
                {activeCount > 0 && <span className={styles.count}>{activeCount}</span>}
            </Button>
        </fieldset>
    );
}
